import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAppId } from '../../context/AppIdContext';
import Header from '../../component/header';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const SocialUpdate = () => {
    const navigate = useNavigate();
    const { appId, userId, language, total_voters } = useAppId();
    const lang = language || 'en';

    // --- Translation Dictionary ---
    const t = {
        en: {
            pageTitle: "Social Updates",
            loadingData: "Loading posts...",
            noData: "No posts available.",
            loadMore: "Load More",
            loadingMore: "Loading...",
            noMorePosts: "You have reached the end.",
            createPostBtn: "Create Post",
            descLabel: "Description",
            descPlaceholder: "Write Description",
            addToPost: "Add to your post",
            btnSubmit: "Create Post",
            creating: "Creating...",
            viewDoc: "View Document",
            socialFeed: "Social Feed",
            socialMedia: "Social Media",
            totalVoters: "TOTAL VOTERS",
            socialActivity: "SOCIAL FILTER VOTER ACTIVITY",
            byBooth: "Booth wise",
            byAddress: "Address wise",
            byPhonebook: "Phonebook wise"
        },
        hi: {
            pageTitle: "सोशल अपडेट्स",
            loadingData: "पोस्ट लोड हो रहे हैं...",
            noData: "कोई पोस्ट उपलब्ध नहीं है।",
            loadMore: "और लोड करें",
            loadingMore: "लोड हो रहा है...",
            noMorePosts: "आप अंत तक पहुँच गए हैं।",
            createPostBtn: "पोस्ट बनाएं",
            descLabel: "विवरण",
            descPlaceholder: "विवरण लिखें",
            addToPost: "अपनी पोस्ट में जोड़ें",
            btnSubmit: "पोस्ट बनाएं",
            creating: "बना रहा है...",
            viewDoc: "दस्तावेज़ देखें",
            socialFeed: "सोशल फीड",
            socialMedia: "सोशल मीडिया",
            totalVoters: "TOTAL VOTERS",
            socialActivity: "SOCIAL ACTIVITY",
            byBooth: "बूथ के अनुसार",
            byAddress: "पते के अनुसार",
            byPhonebook: "फोनबुक के अनुसार"
        },
        gu: {
            pageTitle: "સોશિયલ અપડેટ્સ",
            loadingData: "પોસ્ટ્સ લોડ થઈ રહી છે...",
            noData: "કોઈ પોસ્ટ ઉપલબ્ધ નથી.",
            loadMore: "વધુ જુઓ",
            loadingMore: "લોડ થઈ રહ્યું છે...",
            noMorePosts: "તમે અંત સુધી પહોંચી ગયા છો.",
            createPostBtn: "પોસ્ટ બનાવો",
            descLabel: "વર્ણન",
            descPlaceholder: "Write Description",
            addToPost: "Add to your post",
            btnSubmit: "પોસ્ટ બનાવો",
            creating: "બનાવી રહ્યા છીએ...",
            viewDoc: "ડોક્યુમેન્ટ જુઓ",
            socialFeed: "Social Feed",
            socialMedia: "Social Media",
            totalVoters: "TOTAL VOTERS",
            socialActivity: "SOCIAL ACTIVITY",
            byBooth: "બૂથ પ્રમાણે",
            byAddress: "સરનામું પ્રમાણે",
            byPhonebook: "ફોનબુક પ્રમાણે"
        }
    };

    const txt = t[lang] || t.gu;

    // --- Mobile View State ---
    const [mobileView, setMobileView] = useState('feed'); // 'feed' | 'media'
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);

    // --- Social Media Dashboard State ---
    const [socialMediaData, setSocialMediaData] = useState({
        totalRecords: 0,
        totalFacebook: 0,
        totalInstagram: 0
    });
    const [dashboardLoading, setDashboardLoading] = useState(true);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 992);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    // --- Gallery Modal State ---
    const [modalMediaList, setModalMediaList] = useState([]);
    const [modalCurrentIndex, setModalCurrentIndex] = useState(0);

    // --- Create Post Modal State ---
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [postContent, setPostContent] = useState("");
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const fileInputRef = useRef(null);

    const FETCH_LIMIT = 10;

    useEffect(() => {
        if (!appId || !userId) {
            navigate('/login');
            return;
        }
        fetchPosts(0, true);
        fetchSocialMediaDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, userId]);

    const fetchPosts = async (currentOffset, isInitial = false) => {
        if (isInitial) setLoading(true);
        else setLoadingMore(true);

        try {
            const response = await axios.post(`${BASE_URL}/api/display_posts`, {
                app_id: appId,
                currentUserId: userId?.toString(),
                offset: currentOffset.toString(),
                fetch: FETCH_LIMIT.toString()
            });

            if (response.data.success && response.data.data && response.data.data.result) {
                const newPosts = response.data.data.result;

                if (isInitial) {
                    setPosts(newPosts);
                } else {
                    setPosts(prev => [...prev, ...newPosts]);
                }

                if (newPosts.length < FETCH_LIMIT) {
                    setHasMore(false);
                }
            } else {
                setHasMore(false);
            }
        } catch (error) {
            console.error("Error fetching posts:", error);
            if (isInitial) setPosts([]);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    const fetchSocialMediaDashboard = async () => {
        setDashboardLoading(true);
        try {
            const response = await axios.post(`${BASE_URL}/api/dis_social_media_dash`, {
                app_id: appId
            });

            if (response.data && response.data.success && response.data.data && response.data.data.result) {
                const result = response.data.data.result[0] || {};
                setSocialMediaData({
                    totalRecords: result.TotalRecords || 0,
                    totalFacebook: result.TotalFacebook || 0,
                    totalInstagram: result.TotalInstagram || 0
                });
            } else {
                setSocialMediaData({
                    totalRecords: 0,
                    totalFacebook: 0,
                    totalInstagram: 0
                });
            }
        } catch (err) {
            console.error("Error fetching social media dashboard:", err);
            setSocialMediaData({
                totalRecords: 0,
                totalFacebook: 0,
                totalInstagram: 0
            });
        } finally {
            setDashboardLoading(false);
        }
    };

    const handleLoadMore = () => {
        const newOffset = offset + FETCH_LIMIT;
        setOffset(newOffset);
        fetchPosts(newOffset, false);
    };

    const parseMedia = (mediaString) => {
        if (!mediaString) return [];
        try {
            return JSON.parse(mediaString);
        } catch (e) {
            console.error("Failed to parse media JSON", e);
            return [];
        }
    };

    const handleShare = (post) => {
        if (navigator.share) {
            navigator.share({
                title: post.user_name,
                text: post.content,
                url: window.location.href,
            }).catch(console.error);
        } else {
            alert("Share feature not supported on this browser.");
        }
    };

    // --- Helpers for Display ---
    const isVideo = (item) => {
        if (item.media_type && item.media_type.toLowerCase() === 'v') return true;
        if (item.media_url && item.media_url.match(/\.(mp4|webm|ogg|mov)$/i)) return true;
        return false;
    };

    const isDocument = (item) => {
        if (item.media_type && item.media_type.toLowerCase() === 'd') return true;
        if (item.media_url && item.media_url.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt)$/i)) return true;
        return false;
    };

    const openGallery = (mediaItems, startIndex) => {
        const viewableMedia = mediaItems.filter(item => !isDocument(item));
        if (viewableMedia.length > 0) {
            const targetItem = mediaItems[startIndex];
            let newIndex = viewableMedia.findIndex(v => v.media_url === targetItem?.media_url);
            if (newIndex === -1) newIndex = 0;

            setModalMediaList(viewableMedia);
            setModalCurrentIndex(newIndex);
        }
    };

    const nextMedia = (e) => {
        e.stopPropagation();
        setModalCurrentIndex((prev) => (prev + 1) % modalMediaList.length);
    };

    const prevMedia = (e) => {
        e.stopPropagation();
        setModalCurrentIndex((prev) => (prev - 1 + modalMediaList.length) % modalMediaList.length);
    };

    // --- File Processing for Upload ---
    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(',')[1]);
            reader.onerror = (error) => reject(error);
        });
    };

    const compressImage = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const MAX_WIDTH = 800;
                    const MAX_HEIGHT = 800;
                    let width = img.width;
                    let height = img.height;

                    if (width > height) {
                        if (width > MAX_WIDTH) {
                            height *= MAX_WIDTH / width;
                            width = MAX_WIDTH;
                        }
                    } else {
                        if (height > MAX_HEIGHT) {
                            width *= MAX_HEIGHT / height;
                            height = MAX_HEIGHT;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5).split(',')[1];
                    resolve(compressedBase64);
                };
                img.onerror = (err) => reject(err);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    const handleFileSelect = (e) => {
        if (e.target.files) {
            const filesArray = Array.from(e.target.files);
            setSelectedFiles(prev => [...prev, ...filesArray]);
        }
    };

    const handleCreatePost = async () => {
        if (!postContent.trim() && selectedFiles.length === 0) {
            alert("Please write a description or select an image/video/document.");
            return;
        }

        setIsSubmitting(true);
        try {
            const mediaArray = await Promise.all(selectedFiles.map(async (file) => {
                let b64 = "";
                let mType = "image";
                let fileName = file.name;

                if (file.type.startsWith('image/')) {
                    b64 = await compressImage(file);
                    mType = "image";
                    fileName = fileName.replace(/\.[^/.]+$/, "") + ".jpg";
                } else if (file.type.startsWith('video/')) {
                    b64 = await fileToBase64(file);
                    mType = "video";
                } else {
                    b64 = await fileToBase64(file);
                    mType = "document";
                }

                return {
                    media_type: mType,
                    media_base64: b64,
                    media_url: fileName,
                    media_name: fileName
                };
            }));

            const mediaListJson = JSON.stringify(mediaArray);

            const payload = {
                app_id: String(appId),
                userId: String(userId),
                content: postContent || "",
                mediaListJson: mediaListJson
            };

            const response = await axios.post(`${BASE_URL}/api/ins_post`, payload);

            if (response.data.success || response.data.Success === "1") {
                alert("Post created successfully!");
                setIsCreateModalOpen(false);
                setPostContent("");
                setSelectedFiles([]);
                setOffset(0);
                fetchPosts(0, true);
                fetchSocialMediaDashboard();
            } else {
                alert("Failed to create post: " + JSON.stringify(response.data));
            }
        } catch (error) {
            console.error("Create post error:", error);
            if (error.response) {
                alert("Error from server: " + JSON.stringify(error.response.data));
            } else {
                alert("An error occurred while creating the post. Make sure the file is not too large.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderMediaItem = (item) => {
        if (isVideo(item)) {
            return (
                <video
                    src={item.media_url}
                    controls
                    style={{ width: '100%', height: '100%', objectFit: 'cover', background: '#000' }}
                    onClick={(e) => e.stopPropagation()}
                />
            );
        } else if (isDocument(item)) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#f1f5f9' }}>
                    <svg width="48" height="48" fill="none" stroke="#3b82f6" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <a href={item.media_url} target="_blank" rel="noopener noreferrer" style={{ marginTop: '8px', fontSize: '14px', fontWeight: 'bold', color: '#3b82f6', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
                        {txt.viewDoc}
                    </a>
                </div>
            );
        } else {
            return <img src={item.media_url} alt="Post media" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
        }
    };

    return (
        <>
            <div className={`social-page-container ${isCreateModalOpen || modalMediaList.length > 0 ? 'blur-background' : ''}`}>
                <div className="header-wrapper shadow-sm">
                    <Header title={txt.pageTitle} />
                </div>

                <div className="content-container container-fluid p-3 p-md-4 mx-auto" style={{ maxWidth: '1200px' }}>

                    {/* --- MOBILE TOGGLE BUTTONS --- */}
                    {isMobile && (
                        <div className="mobile-toggle-container slide-up-element mt-2">
                            <button
                                className={`mobile-toggle-btn ${mobileView === 'feed' ? 'active' : ''}`}
                                onClick={() => setMobileView('feed')}
                            >
                                {txt.socialFeed}
                            </button>
                            <button
                                className={`mobile-toggle-btn ${mobileView === 'media' ? 'active' : ''}`}
                                onClick={() => setMobileView('media')}
                            >
                                {txt.socialMedia}
                            </button>
                        </div>
                    )}

                    {/* --- TWO COLUMN LAYOUT --- */}
                    <div className="two-column-layout">

                        {/* LEFT COLUMN - SOCIAL FEED */}
                        <div className={`left-column white-panel slide-up-element ${isMobile && mobileView !== 'feed' ? 'mobile-hidden' : ''}`}>
                            {/* Section Title - SOCIAL FEED */}
                            <div className="section-header-custom">
                                <div className="header-icon-box">
                                    <svg width="22" height="22" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M4 6h2v2H4zm0 5h2v2H4zm0 5h2v2H4zm16-8V6H8v2h12zm0 5v-2H8v2h12zm0 5v-2H8v2h12z" />
                                    </svg>
                                </div>
                                <h2 className="header-title-text text-uppercase">{txt.socialFeed}</h2>
                            </div>

                            {loading ? (
                                <div className="text-center p-5">
                                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <p className="mt-3 text-muted font-weight-bold">{txt.loadingData}</p>
                                </div>
                            ) : posts.length > 0 ? (
                                <div className="d-flex flex-column gap-4 post-feed-wrapper" style={{ animationDelay: '0.2s' }}>
                                    {posts.map((post, index) => {
                                        const mediaItems = parseMedia(post.media);

                                        return (
                                            <div className="post-card" key={post.post_id || index}>

                                                <div className="post-header">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <div className="user-avatar">
                                                            {post.user_photo_path ? (
                                                                <img src={post.user_photo_path} alt={post.user_name} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                                            ) : null}
                                                            <div className="avatar-placeholder" style={{ display: post.user_photo_path ? 'none' : 'flex' }}>
                                                                {post.user_name ? post.user_name.charAt(0).toUpperCase() : 'U'}
                                                            </div>
                                                        </div>
                                                        <div className="user-info">
                                                            <h3 className="user-name">{post.user_name || "Unknown User"}</h3>
                                                            {post.create_date && <span className="post-time">{post.create_date}</span>}
                                                        </div>
                                                    </div>

                                                    <button className="header-share-btn" onClick={() => handleShare(post)}>
                                                        <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                                        </svg>
                                                    </button>
                                                </div>

                                                {post.content && (
                                                    <div className="post-content">
                                                        <p>{post.content}</p>
                                                    </div>
                                                )}

                                                {mediaItems.length > 0 && (
                                                    <div className={`post-media-grid layout-${Math.min(mediaItems.length, 4)}`}>
                                                        {mediaItems.slice(0, 4).map((item, mIndex) => (
                                                            <div
                                                                className="media-item-wrapper"
                                                                key={item.media_id || mIndex}
                                                                onClick={() => {
                                                                    if (!isDocument(item)) {
                                                                        openGallery(mediaItems, mIndex);
                                                                    }
                                                                }}
                                                                style={{ cursor: isDocument(item) ? 'default' : 'pointer' }}
                                                            >
                                                                {renderMediaItem(item)}

                                                                {mIndex === 3 && mediaItems.length > 4 && (
                                                                    <div className="more-media-overlay">
                                                                        <span>+{mediaItems.length - 4}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                            </div>
                                        );
                                    })}

                                    <div className="text-center mt-3 mb-5">
                                        {hasMore ? (
                                            <button
                                                className="btn-load-more"
                                                onClick={handleLoadMore}
                                                disabled={loadingMore}
                                            >
                                                {loadingMore ? txt.loadingMore : txt.loadMore}
                                            </button>
                                        ) : (
                                            <p className="text-muted fw-bold">{txt.noMorePosts}</p>
                                        )}
                                    </div>

                                </div>
                            ) : (
                                <div className="text-center text-muted p-5 bg-white rounded shadow-sm border">
                                    <div style={{ fontSize: '50px', marginBottom: '16px' }}>📭</div>
                                    <h4>{txt.noData}</h4>
                                </div>
                            )}
                        </div>

                        {/* RIGHT COLUMN - SOCIAL MEDIA DASHBOARD */}
                        <div className={`right-column white-panel slide-up-element ${isMobile && mobileView !== 'media' ? 'mobile-hidden' : ''}`} style={{ animationDelay: '0.15s' }}>
                            {/* Section Title - SOCIAL MEDIA */}
                            <div className="section-header-custom">
                                <div className="header-icon-box">
                                    <svg width="22" height="22" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M7 17h2v-7H7v7zm4 0h2V7h-2v10zm4 0h2v-4h-2v4z" />
                                    </svg>
                                </div>
                                <h2 className="header-title-text text-uppercase">{txt.socialMedia}</h2>
                            </div>

                            <div className="social-media-dashboard">

                                {/* Total Voters Card */}
                                <div className="sm-total-card">
                                    <div className="sm-total-icon">
                                        <svg width="24" height="24" fill="currentColor" viewBox="0 0 24 24"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg>
                                    </div>
                                    <div className="sm-total-info">
                                        <div className="sm-total-title">{txt.totalVoters}</div>
                                        <div className="sm-total-count text-success">{total_voters?.toLocaleString()}</div>
                                    </div>
                                </div>

                                {/* Platform Cards */}
                                <div className="sm-platform-row">
                                    <div className="sm-platform-card fb-card">
                                        <div className="sm-platform-icon fb-icon">
                                            <svg width="28" height="28" fill="currentColor" viewBox="0 0 24 24"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z" /></svg>
                                        </div>
                                        <div className="sm-platform-name fb-text">FACEBOOK</div>
                                        <div className="sm-platform-count">{dashboardLoading ? '...' : socialMediaData.totalFacebook.toLocaleString()}</div>
                                    </div>
                                    <div className="sm-platform-card ig-card">
                                        <div className="sm-platform-icon ig-icon">
                                            <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>
                                        </div>
                                        <div className="sm-platform-name ig-text">INSTAGRAM</div>
                                        <div className="sm-platform-count">{dashboardLoading ? '...' : socialMediaData.totalInstagram.toLocaleString()}</div>
                                    </div>
                                </div>

                                {/* Activity Section */}
                                <div className="sm-activity-section">
                                    <h4 className="sm-activity-title">{txt.socialActivity}</h4>

                                    <div className="sm-activity-list">

                                        <div className="sm-activity-item border-left-blue" onClick={() => navigate('/dis_booth_wise_social_media_summary')}>
                                            <div className="sm-activity-item-icon bg-blue-light">
                                                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 9h-2V7h-2v5H6v2h2v5h2v-5h2v-2z" /></svg>
                                            </div>
                                            <span className="sm-activity-item-text">{txt.byBooth}</span>
                                            <svg className="sm-activity-arrow" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </div>

                                        <div className="sm-activity-item border-left-green" onClick={() => navigate('/dis_address_wise_social_media_summary')}>
                                            <div className="sm-activity-item-icon bg-green-light">
                                                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" /><circle cx="12" cy="10" r="3" /></svg>
                                            </div>
                                            <span className="sm-activity-item-text">{txt.byAddress}</span>
                                            <svg className="sm-activity-arrow" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </div>

                                        <div className="sm-activity-item border-left-purple" onClick={() => navigate('/dis_phonebook_wise_social_media_summary')}>
                                            <div className="sm-activity-item-icon bg-purple-light">
                                                <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24"><path d="M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z" /></svg>
                                            </div>
                                            <span className="sm-activity-item-text">{txt.byPhonebook}</span>
                                            <svg className="sm-activity-arrow" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- CREATE POST MODAL --- */}
            {isCreateModalOpen && (
                <div className="create-modal-overlay">
                    <div className="create-modal-box slide-up-element">
                        <div className="modal-top-bar">
                            <button className="modal-close-icon" onClick={() => { setIsCreateModalOpen(false); setPostContent(""); setSelectedFiles([]); }}>
                                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <div className="modal-body-padding">
                            <h3 className="modal-title-bold">{txt.descLabel}</h3>
                            <textarea
                                className="modal-textarea"
                                placeholder={txt.descPlaceholder}
                                value={postContent}
                                onChange={(e) => setPostContent(e.target.value)}
                            ></textarea>

                            {selectedFiles.length > 0 && (
                                <div className="selected-files-preview mt-2">
                                    <span style={{ fontSize: '13px', color: '#10b981', fontWeight: 'bold' }}>{selectedFiles.length} file(s) selected ✅</span>
                                </div>
                            )}
                        </div>

                        <div className="add-to-post-box">
                            <span className="add-to-post-text">{txt.addToPost}</span>
                            <div className="add-icons">
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    style={{ display: 'none' }}
                                />
                                <button className="add-icon-btn text-success" onClick={() => fileInputRef.current.click()}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" /></svg>
                                </button>
                                <button className="add-icon-btn text-primary" onClick={() => fileInputRef.current.click()}>
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg>
                                </button>
                            </div>
                        </div>

                        <div className="modal-footer-padding">
                            <button className="btn-create-post-blue" onClick={handleCreatePost} disabled={isSubmitting}>
                                {isSubmitting ? txt.creating : txt.btnSubmit}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- MULTIPLE MEDIA GALLERY MODAL --- */}
            {modalMediaList.length > 0 && (
                <div className="image-modal-overlay" onClick={() => { setModalMediaList([]); setModalCurrentIndex(0); }}>
                    <button className="image-modal-close" onClick={() => { setModalMediaList([]); setModalCurrentIndex(0); }}>
                        <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>

                    {/* Image Counter */}
                    <div className="gallery-counter">
                        {modalCurrentIndex + 1} / {modalMediaList.length}
                    </div>

                    {/* Previous Button */}
                    {modalMediaList.length > 1 && (
                        <button className="gallery-nav prev" onClick={prevMedia}>
                            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                    )}

                    <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
                        {isVideo(modalMediaList[modalCurrentIndex]) ? (
                            <video
                                src={modalMediaList[modalCurrentIndex].media_url}
                                controls
                                autoPlay
                                className="image-modal-img"
                                style={{ background: 'black' }}
                            />
                        ) : (
                            <img
                                src={modalMediaList[modalCurrentIndex].media_url}
                                alt="Fullscreen View"
                                className="image-modal-img"
                            />
                        )}
                    </div>

                    {/* Next Button */}
                    {modalMediaList.length > 1 && (
                        <button className="gallery-nav next" onClick={nextMedia}>
                            <svg width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        </button>
                    )}
                </div>
            )}

            <style jsx>{`
                .blur-background { filter: blur(4px); pointer-events: none; transition: filter 0.3s ease; }
                
                .social-page-container { width: 100%; min-height: 100vh; background: #eef2f6; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; padding-bottom: 40px; }
                .header-wrapper { background: white; padding: 0 24px; position: sticky; top: 0; z-index: 50; }

                /* --- CUSTOM SECTION HEADER STYLES --- */
                .section-header-custom {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 24px;
                    padding-bottom: 0;
                    border-bottom: none;
                }

                .header-icon-box {
                    width: 44px;
                    height: 44px;
                    background: #e0e7ff; 
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #4f46e5; 
                    border-left: 5px solid #4f46e5; 
                    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
                }

                .header-title-text {
                    font-size: 18px;
                    font-weight: 800;
                    color: #1e293b;
                    margin: 0;
                    letter-spacing: 0.5px;
                }

                .text-uppercase {
                    text-transform: uppercase;
                }

                /* --- MOBILE TOGGLE BUTTONS --- */
                .mobile-toggle-container {
                    display: none;
                    justify-content: center;
                    gap: 12px;
                    margin-bottom: 20px;
                    background: #e2e8f0;
                    padding: 6px;
                    border-radius: 40px;
                    width: fit-content;
                    margin-left: auto;
                    margin-right: auto;
                }
                
                .mobile-toggle-btn {
                    border: none;
                    background: transparent;
                    padding: 10px 28px;
                    border-radius: 34px;
                    font-weight: 700;
                    font-size: 14px;
                    color: #64748b;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }
                
                .mobile-toggle-btn.active {
                    background: #ffffff;
                    color: #0f172a;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }

                /* --- TWO COLUMN LAYOUT CSS --- */
                .two-column-layout {
                    display: flex;
                    gap: 24px;
                    align-items: flex-start;
                }

                .white-panel {
                    background: #ffffff;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
                    border: 1px solid #f1f5f9;
                }

                .left-column {
                    flex: 1;
                    min-width: 0;
                }

                .right-column {
                    flex: 1;
                    min-width: 0;
                    position: sticky;
                    top: 90px;
                    align-self: flex-start;
                }

                /* --- SOCIAL MEDIA DASHBOARD CSS --- */
                .social-media-dashboard {
                    display: flex;
                    flex-direction: column;
                    gap: 16px;
                }
                .sm-total-card {
                    background: #f0fdf4;
                    border: 1px solid #bbf7d0;
                    border-radius: 12px;
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                .sm-total-icon {
                    width: 48px;
                    height: 48px;
                    background: #dcfce7;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #16a34a;
                }
                .sm-total-info { display: flex; flex-direction: column; }
                .sm-total-title { font-size: 12px; font-weight: 700; color: #166534; letter-spacing: 0.5px; margin-bottom: 4px; }
                .sm-total-count { font-size: 28px; font-weight: 800; line-height: 1; }
                .text-success { color: #16a34a; } 

                .sm-platform-row { display: flex; gap: 16px; }
                .sm-platform-card { flex: 1; border-radius: 12px; padding: 24px; display: flex; flex-direction: column; align-items: center; text-align: center; }
                .fb-card { background: #f8fafc; border: 1px solid #e2e8f0; } 
                .ig-card { background: #fff1f2; border: 1px solid #ffe4e6; } 
                
                .sm-platform-icon { width: 56px; height: 56px; border-radius: 16px; display: flex; align-items: center; justify-content: center; margin-bottom: 12px; background: white; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
                .fb-icon { color: #1877f2; }
                .ig-icon { color: #e1306c; }

                .sm-platform-name { font-size: 11px; font-weight: 800; margin-bottom: 8px; letter-spacing: 0.5px; }
                .fb-text { color: #1d4ed8; }
                .ig-text { color: #be185d; }
                
                .sm-platform-count { font-size: 22px; font-weight: 900; color: #0f172a; }

                .sm-activity-section { margin-top: 8px; }
                .sm-activity-title { font-size: 13px; font-weight: 800; color: #1e293b; margin-bottom: 16px; letter-spacing: 0.5px; text-transform: uppercase; }
                
                .sm-activity-list { display: flex; flex-direction: column; gap: 12px; }
                .sm-activity-item {
                    background: white;
                    border-radius: 12px;
                    padding: 16px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    border: 1px solid #f1f5f9;
                    box-shadow: 0 2px 6px rgba(0,0,0,0.02);
                    cursor: pointer;
                    transition: transform 0.2s;
                }
                .sm-activity-item:hover { transform: translateX(4px); }
                
                .border-left-blue { border-left: 4px solid #3b82f6; }
                .border-left-green { border-left: 4px solid #10b981; }
                .border-left-purple { border-left: 4px solid #8b5cf6; }

                .sm-activity-item-icon { width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
                .bg-blue-light { background: #eff6ff; color: #3b82f6; }
                .bg-green-light { background: #f0fdf4; color: #10b981; }
                .bg-purple-light { background: #f5f3ff; color: #8b5cf6; }

                .sm-activity-item-text { flex: 1; font-weight: 700; font-size: 15px; color: #0f172a; }
                .sm-activity-arrow { color: #94a3b8; }

                /* --- POST CARDS --- */
                .create-post-bar { display: flex; justify-content: flex-end; }
                .btn-open-create-modal { display: flex; align-items: center; gap: 8px; background: #3b82f6; color: white; border: none; padding: 10px 20px; border-radius: 8px; font-weight: 600; font-size: 15px; cursor: pointer; box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3); transition: 0.2s; }
                .btn-open-create-modal:hover { background: #2563eb; transform: translateY(-2px); }

                .post-card { 
                    background: #ffffff; 
                    border-radius: 12px; 
                    border: 1px solid #e2e8f0; 
                    box-shadow: 0 1px 3px rgba(0,0,0,0.02); 
                    overflow: hidden; 
                    display: flex; 
                    flex-direction: column; 
                }

                .post-header { display: flex; align-items: center; justify-content: space-between; padding: 16px; border-bottom: 1px solid #f8fafc; }
                .user-avatar { width: 40px; height: 40px; border-radius: 50%; background: #f1f5f9; overflow: hidden; flex-shrink: 0; border: 1px solid #e2e8f0; }
                .user-avatar img { width: 100%; height: 100%; object-fit: cover; }
                .avatar-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; font-weight: bold; color: #475569; background: #e2e8f0; }
                .user-info { display: flex; flex-direction: column; }
                .user-name { margin: 0; font-size: 15px; font-weight: 700; color: #1e293b; }
                .post-time { font-size: 12px; color: #94a3b8; font-weight: 500; margin-top: 2px; }

                .header-share-btn { background: transparent; border: none; color: #94a3b8; cursor: pointer; padding: 8px; display: flex; align-items: center; justify-content: center; transition: all 0.2s; }
                .header-share-btn:hover { color: #3b82f6; }

                .post-content { padding: 16px; }
                .post-content p { margin: 0; font-size: 14px; color: #475569; line-height: 1.5; white-space: pre-wrap; }

                .post-media-grid { display: grid; gap: 2px; background: #f8fafc; }
                .media-item-wrapper { position: relative; width: 100%; padding-top: 100%; overflow: hidden; background: #e2e8f0; }
                .media-item-wrapper img, .media-item-wrapper video, .media-item-wrapper div { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }
                
                .layout-1 { grid-template-columns: 1fr; }
                .layout-1 .media-item-wrapper { padding-top: 60%; }
                .layout-2 { grid-template-columns: 1fr 1fr; }
                .layout-3 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
                .layout-3 .media-item-wrapper:first-child { grid-row: 1 / span 2; padding-top: 200%; }
                .layout-4 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }

                .more-media-overlay { position: absolute; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6) !important; display: flex !important; align-items: center; justify-content: center; color: white; font-size: 1.5rem; font-weight: 700; z-index: 10; pointer-events: none; }

                .btn-load-more { background: white; color: #3b82f6; border: 1px solid #bfdbfe; padding: 10px 24px; border-radius: 30px; font-weight: 600; font-size: 14px; cursor: pointer; transition: all 0.2s; box-shadow: 0 2px 4px rgba(0,0,0,0.02); }
                .btn-load-more:hover:not(:disabled) { background: #eff6ff; border-color: #93c5fd; }
                .btn-load-more:disabled { opacity: 0.7; cursor: not-allowed; }

                /* MODAL CSS REMAINING UNCHANGED */
                .create-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 10000; padding: 20px; }
                .create-modal-box { background: #eef2f6; border-radius: 12px; width: 100%; max-width: 450px; overflow: hidden; box-shadow: 0 10px 25px rgba(0,0,0,0.2); display: flex; flex-direction: column; }
                .modal-top-bar { display: flex; justify-content: flex-end; padding: 10px; }
                .modal-close-icon { background: none; border: none; color: #64748b; cursor: pointer; }
                .modal-body-padding { padding: 0 20px 20px 20px; }
                .modal-title-bold { font-size: 18px; font-weight: 800; color: #1e293b; margin: 0 0 12px 0; }
                .modal-textarea { width: 100%; height: 180px; padding: 15px; border-radius: 8px; border: 1px solid #e2e8f0; font-size: 15px; resize: none; outline: none; background: white; }
                .modal-textarea:focus { border-color: #cbd5e1; }
                
                .add-to-post-box { background: #e2e8f0; margin: 0 20px 20px 20px; padding: 12px 16px; border-radius: 8px; display: flex; justify-content: space-between; align-items: center; }
                .add-to-post-text { font-size: 14px; font-weight: 700; color: #1e293b; }
                .add-icons { display: flex; gap: 12px; }
                .add-icon-btn { background: none; border: none; cursor: pointer; padding: 0; transition: transform 0.2s; }
                .add-icon-btn:hover { transform: scale(1.1); }
                .text-success { color: #22c55e; }
                .text-primary { color: #3b82f6; }

                .modal-footer-padding { padding: 0 20px 20px 20px; }
                .btn-create-post-blue { width: 100%; padding: 14px; border-radius: 8px; background: #4f46e5; color: white; border: none; font-size: 16px; font-weight: 600; cursor: pointer; transition: 0.2s; }
                .btn-create-post-blue:hover:not(:disabled) { background: #4338ca; }
                .btn-create-post-blue:disabled { opacity: 0.7; cursor: not-allowed; }

                .image-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.95); display: flex; align-items: center; justify-content: center; z-index: 10000; padding: 20px; }
                .image-modal-close { position: absolute; top: 20px; right: 20px; background: rgba(255,255,255,0.2); border: none; color: white; width: 44px; height: 44px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 10001; transition: 0.2s; }
                .image-modal-close:hover { background: rgba(255,255,255,0.4); }
                
                .gallery-counter { position: absolute; top: 30px; left: 50%; transform: translateX(-50%); color: white; font-weight: 600; font-size: 16px; background: rgba(0,0,0,0.5); padding: 4px 12px; border-radius: 20px; z-index: 10001; }
                
                .gallery-nav { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(255,255,255,0.1); border: none; color: white; width: 50px; height: 50px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; z-index: 10001; transition: 0.2s; }
                .gallery-nav:hover { background: rgba(255,255,255,0.3); }
                .gallery-nav.prev { left: 20px; }
                .gallery-nav.next { right: 20px; }

                .image-modal-content { max-width: 90vw; max-height: 90vh; display: flex; align-items: center; justify-content: center; }
                .image-modal-img { max-width: 100%; max-height: 90vh; object-fit: contain; border-radius: 8px; }

                .slide-up-element { animation: slideUp 0.4s ease forwards; opacity: 0; }
                @keyframes slideUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }

                /* 🔴 FIX: Mobile specifically to remove padding/margin from social feed */
                @media (max-width: 992px) {
                    .mobile-toggle-container {
                        display: flex;
                    }
                    .mobile-hidden {
                        display: none !important;
                    }
                    .two-column-layout {
                        flex-direction: column;
                        gap: 16px;
                    }
                    .left-column, .right-column {
                        flex: 1;
                        width: 100%;
                        position: static;
                    }
                }

                @media (max-width: 768px) {
                    /* Remove side padding from main container so feed touches edges */
                    .content-container {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                    
                    /* Adjust the toggle buttons margin since container padding is 0 */
                    .mobile-toggle-container {
                        margin-top: 10px;
                    }

                    /* 🔴 LEFT COLUMN (SOCIAL FEED) - Remove padding and background */
                    .left-column.white-panel {
                        padding: 12px 0 0 0;
                        background: transparent;
                        border: none;
                        box-shadow: none;
                    }
                    
                    /* Adjust header padding so it doesn't touch the very edge */
                    .left-column .section-header-custom {
                        padding: 0 16px;
                    }

                    /* Adjust post card to span full width and remove borders */
                    .post-card {
                        border-radius: 0;
                        border-left: none;
                        border-right: none;
                        margin-bottom: 8px; /* Adds gap between posts like real apps */
                    }
                    
                    /* We removed main container padding, so Right Column needs its padding back */
                    .right-column.white-panel {
                        margin: 0 16px;
                        width: calc(100% - 32px);
                    }
                    .gap-4 {
                        gap: 0rem !important;
                    }
                }

            `}</style>
        </>
    );
};

export default SocialUpdate;